import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../config/supabase';
import { toast } from 'react-toastify';
import { createCourseLead } from '../utils/leads';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTenantSlug } from '../context/TenantSlugContext';
import { buildTenantPath } from '../utils/tenantSlug';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const NewsDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const { selectedClient, isLoading: authLoading } = useAuth();
  const { tenantSlug } = useTenantSlug();
  const effectiveSlug = tenantSlug || selectedClient?.clientSlug || null;
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrivacyConsent, setShowPrivacyConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  useEffect(() => {
    const fetchNews = async () => {
      if (!id) return;
      if (authLoading) return;

      if (!effectiveSlug || !UUID_REGEX.test(id)) {
        setNews(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .rpc('get_public_news_detail_by_slug', {
            p_slug: effectiveSlug,
            p_newspage_id: id,
          });

        if (error) {
          throw error;
        }

        const row = Array.isArray(data) ? data[0] : data;
        if (row) {
          setNews(row);
        } else {
          setNews(null);
        }
      } catch (err: any) {
        toast.error('Erro ao carregar notícia.');
        console.error('Erro ao carregar notícia:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [authLoading, effectiveSlug, id]);

  const handleLeadFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLeadFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!leadFormData.name || !leadFormData.email) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }
    
    setPendingFormData(leadFormData);
    setShowPrivacyConsent(true);
  };

  const handlePrivacyConsentSubmit = async () => {
    if (!privacyConsent) {
      toast.error('Você deve concordar com o uso dos seus dados para continuar');
      return;
    }

    if (!pendingFormData) return;

    try {
      setIsSubmitting(true);
      
      await createCourseLead({
        clientId: news.clientId,
        name: pendingFormData.name,
        email: pendingFormData.email,
        phone: pendingFormData.phone,
        profession: '',
        experience: '',
        interests: ['Notícias'],
        message: `Lead gerado a partir da notícia: ${news?.newspageTitle}`,
        selectedCourse: 'Notícias',
        selectedCourseTitle: 'Interesse geral em notícias'
      });
      
      toast.success('Obrigado pelo seu interesse! Entraremos em contato em breve.');
      setShowPrivacyConsent(false);
      setPrivacyConsent(false);
      setPendingFormData(null);
      setShowLeadForm(false);
      setLeadFormData({ name: '', email: '', phone: '' });
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      toast.error('Ocorreu um erro ao enviar seus dados. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrivacyConsentCancel = () => {
    setShowPrivacyConsent(false);
    setPrivacyConsent(false);
    setPendingFormData(null);
  };

  if (authLoading || loading) {
    return <p className="text-center py-8">Carregando notícia...</p>;
  }

  if (!effectiveSlug) {
    return <p className="text-center py-8">Selecione uma clínica para visualizar esta notícia.</p>;
  }

  if (!news) {
    return <p className="text-center py-8">Notícia não encontrada.</p>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to={buildTenantPath("/noticias", tenantSlug)} className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 mb-8">
          <ArrowLeft size={16} className="mr-1" />
          Voltar para as notícias
        </Link>

        <article className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="aspect-w-16 aspect-h-9 overflow-hidden">
            {news.newspageImage && 
              <img
                src={news.newspageImage}
                alt={news.newspageTitle}
                className="w-full h-full object-cover object-center"
              />
            }
          </div>
          <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">{news.newspageTitle}</h1>
            <p className="text-gray-500 text-sm mb-6">
              Publicado em {new Date(news.newspagePublishDate).toLocaleDateString('pt-BR')}
            </p>
            <div className="prose prose-lg max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: news.newspageFormattedContent || news.newspageContent }} />
          </div>
          
          {news.enableLeadCapture && (
            <div className="mt-8">
              {!showLeadForm ? (
                <div className="bg-primary-50 border border-primary-100 rounded-lg p-6 text-center">
                  <h3 className="text-xl font-semibold text-primary-800 mb-4">
                    Quer saber mais sobre este assunto?
                  </h3>
                  <p className="text-primary-700 mb-6">
                    Deixe seus dados para receber mais informações e conteúdos exclusivos.
                  </p>
                  <button
                    onClick={() => setShowLeadForm(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    {news.leadButtonText || 'Quero saber mais'}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="bg-primary-50 border border-primary-100 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-primary-800 mb-4">
                    Preencha seus dados
                  </h3>
                  <form onSubmit={handleLeadSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nome completo *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={leadFormData.name}
                        onChange={handleLeadFormChange}
                        required
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={leadFormData.email}
                        onChange={handleLeadFormChange}
                        required
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Telefone/WhatsApp
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={leadFormData.phone}
                        onChange={handleLeadFormChange}
                        placeholder="(00) 00000-0000"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowLeadForm(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                      >
                        Enviar
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </article>
      </div>

      <AnimatePresence>
        {showPrivacyConsent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Aviso de Privacidade - Consentimento para Uso de Dados
                </h3>
                
                <div className="text-gray-700 space-y-4 mb-6">
                  <p>
                    Nós, da Revalidatie, levamos a sua privacidade muito a sério. Solicitamos que você forneça alguns dados pessoais (nome, endereço de e-mail e telefone) para que possamos entrar em contato com você e enviar nosso informativo semanal por e-mail, com conteúdos sobre saúde, bem-estar e novidades relacionadas aos nossos serviços.
                  </p>
                  
                  <p className="font-medium">
                    Ao marcar a opção abaixo, você confirma que:
                  </p>
                  
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      Autoriza a utilização dos seus dados pessoais fornecidos para que possamos entrar em contato com você, inclusive via telefone e e-mail.
                    </li>
                    <li>
                      Autoriza o envio semanal de e-mails informativos da Revalidatie.
                    </li>
                  </ul>
                  
                  <p className="text-sm">
                    Seus dados serão tratados com segurança, confidencialidade e em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018). Você poderá retirar seu consentimento e solicitar a exclusão dos seus dados da nossa base a qualquer momento através dos canais de contato disponíveis em nosso site.
                  </p>
                </div>
                
                <div className="border-t border-gray-200 pt-4 mb-6">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyConsent}
                      onChange={(e) => setPrivacyConsent(e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      Li, estou ciente e concordo com o uso dos meus dados pessoais conforme descrito acima.
                    </span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handlePrivacyConsentCancel}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handlePrivacyConsentSubmit}
                    disabled={!privacyConsent || isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enviando...
                      </div>
                    ) : (
                      'Confirmar e Enviar'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsDetailPage;
